/**
 * El pitido del lector.
 *
 * At a counter nobody is looking at the phone while they scan — they are looking at the can
 * and the customer. A supermarket scanner answers with a sound for exactly that reason, and
 * this one does too: one short high note when the product was found, two low ones when it was
 * not, so the eyes only go to the screen when there is something to decide. A buzz with it,
 * on the phones that can.
 *
 * Browsers only let a page make sound after somebody has touched it, and Safari wants the
 * audio started from inside that touch — so `prepararSonido` is called by the button that
 * opens the scanner, and the first beep is ready by the time a barcode is.
 */

export type Resultado = 'bien' | 'mal';

let contexto: AudioContext | null = null;

export function prepararSonido(): void {
  try {
    contexto ??= new AudioContext();
    if (contexto.state === 'suspended') void contexto.resume();
  } catch {
    contexto = null;
  }
}

/** [frequency Hz, start s, length s] */
const NOTAS: Record<Resultado, [number, number, number][]> = {
  bien: [[1_800, 0, 0.08]],
  mal: [
    [440, 0, 0.11],
    [330, 0.14, 0.16],
  ],
};

export function pitar(resultado: Resultado): void {
  try {
    navigator.vibrate?.(resultado === 'bien' ? 40 : [70, 60, 70]);
  } catch {
    // Vibration is a courtesy.
  }
  if (!contexto) return;
  const ahora = contexto.currentTime;
  for (const [hz, desde, dura] of NOTAS[resultado]) {
    const oscilador = contexto.createOscillator();
    const volumen = contexto.createGain();
    oscilador.frequency.value = hz;
    // A ramp in and out: a tone that starts and stops dead clicks.
    volumen.gain.setValueAtTime(0.0001, ahora + desde);
    volumen.gain.exponentialRampToValueAtTime(0.25, ahora + desde + 0.01);
    volumen.gain.exponentialRampToValueAtTime(0.0001, ahora + desde + dura);
    oscilador.connect(volumen).connect(contexto.destination);
    oscilador.start(ahora + desde);
    oscilador.stop(ahora + desde + dura + 0.02);
  }
}
